import React, { useState, useEffect } from 'react';
import { notify } from '../Notifications';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import GeneratedFiles from '../GeneratedFiles';
import FeedbackModal from '../FeedbackModal';
import { submitFeedback, setAuthToken } from '../../services/api';

import CodeBlock from './CodeBlock';
import Markdown from './Markdown';
import CollapsibleCodeExecution from './CollapsibleCodeExecution';
import ImageGenerationResult from './ImageGenerationResult';
import WebSearchResults from './WebSearchResults';
import SharePointResults, { SharePointStatus, mergeSharePointSources } from './SharePointResults';
import WebSearchNudge from './WebSearchNudge';
import ActivityStatus from './ActivityStatus';
import { SHORTCUTS, formatShortcut } from '../../hooks/useKeyboardShortcuts';
import {
  CheckIcon, CopyIcon, DownloadIcon, FeedbackIcon, FileIcon, GlobeIcon, PencilIcon, RegenerateIcon, StopCircleIcon,
} from '../icons';
import {
  SnowflakeThinking,
  SnowflakeChart,
  SnowflakeTable,
  SnowflakeStatus,
  SnowflakeToolStatus,
} from '../SnowflakeContent';
import type { ChatMessageProps, ContentPart, SharePointResultsPart } from './types';

// ── Authenticated image loader ────────────────────────────────────────
// Fetches an image via download_url with auth headers and displays it.
const AuthImage: React.FC<{
  downloadUrl: string;
  alt: string;
  style?: React.CSSProperties;
  getAccessToken: () => Promise<string | null>;
}> = ({ downloadUrl, alt, style, getAccessToken }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    const absoluteUrl = downloadUrl.startsWith('http') ? downloadUrl : `${API_BASE_URL}${downloadUrl}`;

    (async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(absoluteUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) 
          return;

        const blob = await res.blob();

        if (revoked) 
          return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch { /* ignore */ }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [downloadUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!blobUrl) return <div style={{ ...style, backgroundColor: 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>{alt}</div>;
  return <img src={blobUrl} alt={alt} style={style} />;
};

// Inline editor for the last question; Enter sends, Shift+Enter is a new line, Esc cancels.
const EditBox: React.FC<{
  initial: string;
  hasFiles: boolean;
  onCancel: () => void;
  onSubmit: (text: string) => void;
}> = ({ initial, hasFiles, onCancel, onSubmit }) => {
  const [text, setText] = useState(initial);
  const canSend = text.trim().length > 0;

  return (
    <div className="cv-edit-box">
      <textarea
        className="cv-edit-box__input"
        aria-label="Edit your message"
        value={text}
        autoFocus
        rows={Math.min(8, Math.max(2, text.split('\n').length))}
        onChange={(e) => setText(e.target.value)}
        onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          } else if (e.key === 'Enter' && !e.shiftKey && canSend) {
            e.preventDefault();
            onSubmit(text);
          }
        }}
      />
      <div className="cv-edit-box__footer">
        {hasFiles && <span className="cv-edit-box__note">Attachments are kept</span>}
        <button type="button" className="cv-tool-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="cv-tool-btn cv-tool-btn--primary" disabled={!canSend} onClick={() => onSubmit(text)}>
          Send
        </button>
      </div>
    </div>
  );
};

// Progress parts that describe what is happening right now rather than content.
const LIVE_STATUS_TYPES = new Set<ContentPart['type']>([
  'sharepoint_status', 'web_search_status', 'snowflake_status', 'snowflake_tool_status',
]);

// ── Main component ────────────────────────────────────────────────────

const ChatMessage: React.FC<ChatMessageProps> = ({
  message, isLoading, isLastMessage, isAnalyzingFiles, conversationId, agentId, messageIndex,
  modelProvider, modelName, modelApiVersion,
  onNudgeRetryWithSearch, onNudgeEnableAuto, onRegenerate, onEdit
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const isStreaming = isLoading && isLastMessage;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownloadMessage = () => {
    try {
      const blob = new Blob([message.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `response-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to download text: ', err);
    }
  };

  const handleFeedbackSubmit = async (feedbackText: string, category?: string) => {
    if (!conversationId || messageIndex === undefined) return;
    try {
      const token = await getAccessToken();
      setAuthToken(token || '');
      await submitFeedback({
        conversation_id: conversationId,
        message_index: messageIndex,
        feedback_text: feedbackText,
        category,
        model_provider: modelProvider,
        model_name: modelName,
        model_api_version: modelApiVersion
      });
    } catch (err) {
      console.error('Failed to submit feedback: ', err);
      throw err;
    }
  };

  const handleDownload = async (file: { name: string; content?: string; type: string; download_url?: string }) => {
    try {
      if (file.content) {
        const byteCharacters = atob(file.content);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: file.type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.name;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (file.download_url) {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const absoluteUrl = file.download_url.startsWith('http')
          ? file.download_url
          : `${API_BASE_URL}${file.download_url}`;

        const accessToken = await getAccessToken();
        const response = await fetch(absoluteUrl, {
          method: 'GET',
          credentials: 'include',
          headers: { ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }) }
        });

        if (!response.ok)
          throw new Error('Failed to download file');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        notify('File download URL not available. Please try re-uploading the file.', 'error');
      }
    } catch (err) {
      console.error('Failed to download file: ', err);
      notify('Failed to download file. Please try again.', 'error');
    }
  };

  // ── Render content parts ─────────────────────────────────────────
  const renderParts = () => {
    if (!message.parts || message.parts.length === 0) return null;

    const sharePointParts = message.parts.filter(
      (p): p is SharePointResultsPart => p.type === 'sharepoint_results'
    );
    const firstSharePointIdx = message.parts.findIndex((p) => p.type === 'sharepoint_results');
    const lastIdx = message.parts.length - 1;
    const lastType = message.parts[lastIdx].type;
    // Without this, a finished-looking card sits motionless while the model is still working.
    const needsWorkingIndicator = isStreaming
      && !LIVE_STATUS_TYPES.has(lastType)
      && (isAnalyzingFiles || (lastType !== 'text' && lastType !== 'web_search_nudge'));

    const rendered = message.parts.map((part, idx) => {
      switch (part.type) {
        case 'text':
          return (
            <Markdown key={idx}>{part.content}</Markdown>
          );
        case 'code_execution':
          return (
            <CollapsibleCodeExecution key={idx}>
              <CodeBlock><code>{part.code}</code></CodeBlock>
            </CollapsibleCodeExecution>
          );
        case 'generated_files':
          return <GeneratedFiles key={idx} files={part.files} />;
        case 'image_generation':
          return <ImageGenerationResult key={idx} imageGeneration={part} />;
        case 'web_search_results':
          return <WebSearchResults key={idx} data={part} />;
        case 'sharepoint_results':
          return idx === firstSharePointIdx
            ? <SharePointResults key={idx} sources={mergeSharePointSources(sharePointParts)} />
            : null;
        case 'sharepoint_status':
          return isStreaming && idx === lastIdx
            ? <SharePointStatus key={idx} status={part.status} />
            : null;
        case 'stopped':
          return (
            <div key={idx} className="cv-stopped-note">
              <StopCircleIcon size={14} />
              Stopped
            </div>
          );
        case 'web_search_status':
          return isStreaming && idx === lastIdx
            ? <ActivityStatus key={idx} icon={<GlobeIcon size={14} />} label="Searching the web…" />
            : null;
        case 'snowflake_thinking':
          return <SnowflakeThinking key={idx} text={part.text} />;
        case 'snowflake_chart':
          return <SnowflakeChart key={idx} spec={part.spec} />;
        case 'snowflake_table':
          return <SnowflakeTable key={idx} data={part} agentId={agentId} />;
        case 'snowflake_status':
          return <SnowflakeStatus key={idx} message={part.message} active={isStreaming && idx === lastIdx} />;
        case 'snowflake_tool_status':
          return <SnowflakeToolStatus key={idx} message={part.message} active={isStreaming && idx === lastIdx} />;
        case 'web_search_nudge':
          return (
            <WebSearchNudge
              key={idx}
              conversationId={part.conversation_id ?? conversationId}
              onRetryWithSearch={onNudgeRetryWithSearch}
              onEnableAuto={onNudgeEnableAuto}
            />
          );
        default:
          return null;
      }
    });

    return (
      <>
        {rendered}
        {needsWorkingIndicator && <ActivityStatus label={isAnalyzingFiles ? 'Analyzing files…' : 'Thinking…'} />}
      </>
    );
  };

  // ── Render message content ───────────────────────────────────────
  const renderContent = () => {
    if (message.role === 'assistant') {
      // Parts-based rendering
      if (message.parts && message.parts.length > 0) {
        return renderParts();
      }

      // Plain markdown fallback (for old messages or simple text)
      if (message.content) {
        return (
          <Markdown>{message.content}</Markdown>
        );
      }

      if (isStreaming) {
        return <ActivityStatus label={isAnalyzingFiles ? 'Analyzing files…' : 'Thinking…'} />;
      }

      return null;
    }

    // User message — plain text
    return <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>;
  };

  return (
    <div className={`cv-message${isLastMessage ? ' cv-message--latest' : ''}`} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: message.role === 'user' ? 'flex-end' : 'stretch',
      width: '100%'
    }}>
      {isEditing && onEdit ? (
        <EditBox
          initial={message.content}
          hasFiles={!!message.files?.length}
          onCancel={() => setIsEditing(false)}
          onSubmit={(text) => { setIsEditing(false); onEdit(text); }}
        />
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: message.role === 'user' ? 'flex-end' : 'stretch',
          width: '100%'
        }}>
          {message.role === 'user' && onEdit && (
            // Beside the bubble rather than under it, so it adds no height to the conversation.
            <div className="cv-message-actions cv-message-actions--inline">
              <button
                type="button"
                className="cv-icon-btn"
                onClick={() => setIsEditing(true)}
                data-tooltip="Edit"
                aria-label="Edit message"
              >
                <PencilIcon />
              </button>
            </div>
          )}
          <div style={message.role === 'user' ? {
            background: colors.userMessageBg,
            color: 'white',
            borderRadius: '18px',
            padding: '10px 16px',
            maxWidth: '70%',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            wordBreak: 'break-word',
            lineHeight: '1.5',
            fontSize: '15px',
          } : {
            // Answers read as the page itself, not a bubble; cards inside keep their own frames.
            color: colors.textPrimary,
            width: '100%',
            wordBreak: 'break-word',
            lineHeight: '1.65',
            fontSize: '15px',
          }}>
            {renderContent()}

            {/* Display attached files */}
            {message.files && message.files.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {message.files[0].type.startsWith('image/') ? (
                  // Image files — show as a row of thumbnails
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {message.files.map((file, idx) => (
                      <div key={idx}>
                        {file.content ? (
                          <img
                            src={`data:${file.type};base64,${file.content}`}
                            alt={file.name}
                            style={{
                              maxWidth: '200px', maxHeight: '150px', borderRadius: '8px',
                              objectFit: 'cover', display: 'block',
                              border: message.role === 'user' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)'
                            }}
                          />
                        ) : file.download_url ? (
                          <AuthImage
                            downloadUrl={file.download_url}
                            alt={file.name}
                            getAccessToken={getAccessToken}
                            style={{
                              maxWidth: '200px', maxHeight: '150px', borderRadius: '8px',
                              objectFit: 'cover', display: 'block',
                              border: message.role === 'user' ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.1)'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '150px', height: '100px', borderRadius: '8px',
                            backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', color: message.role === 'user' ? 'rgba(255,255,255,0.6)' : colors.textMuted
                          }}>
                            {file.name}
                          </div>
                        )}
                        <div style={{
                          fontSize: '11px',
                          color: message.role === 'user' ? 'rgba(255,255,255,0.7)' : colors.textMuted,
                          marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          maxWidth: '200px'
                        }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                          <button
                            onClick={() => handleDownload(file)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              padding: '2px 4px', borderRadius: '4px', fontSize: '11px',
                              color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : colors.textMuted,
                              transition: 'background-color 0.2s ease', flexShrink: 0
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            title="Download file"
                            aria-label={`Download ${file.name}`}
                          >
                            <DownloadIcon size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Non-image files
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                    {message.files.map((file, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center',
                        backgroundColor: message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                        borderRadius: '8px', padding: '6px 8px'
                      }}>
                        <span style={{ marginRight: '8px', display: 'flex', color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : colors.textMuted }}>
                          <FileIcon size={16} />
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '13px', fontWeight: '500',
                            color: message.role === 'user' ? 'rgba(255,255,255,0.9)' : colors.textPrimary
                          }}>
                            {file.name}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: message.role === 'user' ? 'rgba(255,255,255,0.7)' : colors.textMuted
                          }}>
                            {file.size < 1024 ? `${file.size} B` : `${(file.size / 1024).toFixed(1)} KB`}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(file)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: '4px 6px', borderRadius: '4px', fontSize: '14px',
                            color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : colors.textMuted,
                            transition: 'background-color 0.2s ease', marginLeft: '6px'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = message.role === 'user' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          title="Download file"
                          aria-label={`Download ${file.name}`}
                        >
                          <DownloadIcon size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {message.role === 'assistant' && !isStreaming && (message.content || onRegenerate) && (
        <div className="cv-message-actions">
          {message.content && <>
            <button
              type="button"
              className={`cv-icon-btn${copySuccess ? ' is-done' : ''}`}
              onClick={handleCopy}
              data-action="copy-response"
              data-shortcut={isLastMessage ? formatShortcut(SHORTCUTS.copyLastResponse) : undefined}
              data-shortcut-pos="above"
              data-tooltip={copySuccess ? 'Copied' : 'Copy'}
              aria-label={copySuccess ? 'Copied' : 'Copy response'}
            >
              {copySuccess ? <CheckIcon /> : <CopyIcon />}
            </button>
            <button
              type="button"
              className={`cv-icon-btn${downloadSuccess ? ' is-done' : ''}`}
              onClick={handleDownloadMessage}
              data-tooltip={downloadSuccess ? 'Downloaded' : 'Download as .txt'}
              aria-label={downloadSuccess ? 'Downloaded' : 'Download response as .txt file'}
            >
              {downloadSuccess ? <CheckIcon /> : <DownloadIcon />}
            </button>
          </>}
          {onRegenerate && (
            <button
              type="button"
              className="cv-icon-btn"
              onClick={onRegenerate}
              data-tooltip="Regenerate"
              aria-label="Regenerate response"
            >
              <RegenerateIcon />
            </button>
          )}
          {message.content && (
            <button
              type="button"
              className="cv-icon-btn"
              onClick={() => setIsFeedbackModalOpen(true)}
              data-tooltip="Send feedback"
              aria-label="Send feedback about this response"
            >
              <FeedbackIcon />
            </button>
          )}
        </div>
      )}

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
};

export default React.memo(ChatMessage);